import React from 'react';

import cx from 'classnames';
import { AirplaneTilt } from 'phosphor-react';
import { useFormContext } from 'react-hook-form';
import { GroupBase } from 'react-select-5';

import { Alert } from 'components/shared/Alert';
import { HookTimeInput } from 'components/shared/HookFormComponents';
import { Select } from 'components/shared/Select/adapters/HookForm';
import { DeleteAction } from 'components/shared/Select/components/DeleteAction';
import { OptionWithAction } from 'components/shared/Select/options/OptionWithAction';
import { TabLabel } from 'components/shared/TabSelector';
import { EOrganizedBy, IApiFlightTripEvent } from 'detailsv2/structure';
import { useRelocationPoints } from 'hooks/useRelocationPointMutations/relocationPoints';
import { isIDL } from 'utils/date';

import { DayOrDatePicker } from '../../../DayOrDatePicker';
import { FlightTab } from '../../../types';
import s from '../../FlightTabs.module.css';
import { Content } from '../Content';

type ContentProps = {
  tripEvent: IApiFlightTripEvent;
  activeTab: FlightTab;
  isDisabled: boolean;
  organizedBy: EOrganizedBy;
};

type PlaceOption = {
  label: string;
  value: string;
};

export const FlightManualTab = () => <TabLabel Icon={AirplaneTilt} text="Add Flight Manually" />;

export const FlightManualContent = (props: ContentProps) => {
  const methods = useFormContext();
  const isInternationalDateLineCrossed = isIDL(
    methods.watch(`${props.organizedBy}Range.start`),
    methods.watch(`${props.organizedBy}Range.end`),
  );

  const { relocationPoints, deleteRelocationPoint, createRelocationPoint } = useRelocationPoints([
    methods.watch('data.departureFrom'),
    methods.watch('data.arrivalTo'),
  ]);

  const OptionWithDeleteMemoized = React.useCallback(
    <
      Option extends PlaceOption = PlaceOption,
      _IsMulti extends boolean = boolean,
      _Group extends GroupBase<Option> = GroupBase<Option>,
    >(
      props: React.ComponentProps<typeof OptionWithAction<Option, _IsMulti, _Group>>,
    ) => {
      const onClickDelete = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        deleteRelocationPoint(props.data.value);
      };

      return <OptionWithAction {...props} action={<DeleteAction onClick={onClickDelete} />} />;
    },
    [],
  );

  const onCreateRelocationPoint = (inputValue: string) => {
    createRelocationPoint(inputValue);
    return { label: inputValue, value: inputValue };
  };

  return (
    <Content organizedBy={props.organizedBy}>
      <div className={s.title}>Departure</div>
      <div className={cx(s.fieldGroups, s.marginBottom)}>
        <DayOrDatePicker tripEvent={props.tripEvent} isStartDate />
        <HookTimeInput name="data.departureTime" control={methods.control} label="Time" />
        <div>
          <Select
            name="data.departureFrom"
            control={methods.control}
            options={relocationPoints}
            optionToString
            label={() => `Departure From${methods.watch('data.departureFrom') ? '' : '*'}`}
            creatable
            editable
            components={{
              Option: OptionWithDeleteMemoized,
            }}
            onCreateOption={onCreateRelocationPoint}
          />
        </div>
      </div>
      <div className={s.title}>Arrival</div>
      <div className={s.fieldGroups}>
        <DayOrDatePicker tripEvent={props.tripEvent} />
        <HookTimeInput name="data.arrivalTime" control={methods.control} label="Time" />
        <div>
          <Select
            name="data.arrivalTo"
            control={methods.control}
            options={relocationPoints}
            label={() => `Arriving To${methods.watch('data.arrivalTo') ? '' : '*'}`}
            optionToString
            creatable
            editable
            components={{
              Option: OptionWithDeleteMemoized,
            }}
            onCreateOption={onCreateRelocationPoint}
          />
        </div>
      </div>
      {isInternationalDateLineCrossed && (
        <Alert
          variant="info"
          content="Heads up! This flight crosses the International Date Line. The departure day is after the arrival day."
          className={s.idl}
        />
      )}
    </Content>
  );
};
